import {
  czPackingDevServers,
  devServers,
  packingProdServers,
  prodServers,
} from '@labeling/globals';
import {
  Button,
  Card,
  Checkbox,
  HelpText,
  Label,
  Modal,
  TextField,
} from '@lego/connect-components-react';
import { type KeyboardEvent, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Table } from '../../components';
import type { TableColumn } from '../../components/table';
import { useApi } from '../../providers/api';
import { useToastContext } from '../../providers/toast';
import { useUserStore } from '../../stores/user-store';
import { isAdmin } from '../../utils/permissions';
import { getResponseErrorMessage, toErrorMessage } from '../../utils/response';
import { NotFoundPage } from '../not-found';

const SERVER_DASHBOARD_URL = `${import.meta.env.VITE_GRAFANA_URL}/d/windows-services-status/windows-services-status?orgId=1&from=now-1h&to=now&timezone=browser&var-datasource=APP-03500-mimir-prod&var-environment=$__all&var-server=$__all&var-service=$__all`;
const IS_PRODUCTION = import.meta.env.MODE === 'prod';
const SERVERS = import.meta.env.MODE === 'prod' ? prodServers : devServers;
const PACKING_SERVERS = IS_PRODUCTION ? packingProdServers : czPackingDevServers;
const isPackingServer = (server: string) =>
  PACKING_SERVERS.includes(server as (typeof PACKING_SERVERS)[number]);

type SyncType = 'graphics' | 'labels' | 'printers';

const SERVERS_PAGE_TAB = {
  servers: 'servers',
  dataSync: 'data-sync',
} as const;

type ServersPageTab = (typeof SERVERS_PAGE_TAB)[keyof typeof SERVERS_PAGE_TAB];

const SYNC_TYPES: { value: SyncType; label: string }[] = [
  { value: 'graphics', label: 'Graphics' },
  { value: 'labels', label: 'Labels' },
  { value: 'printers', label: 'Printers' },
];

export const ServersPage = () => {
  const { t } = useTranslation();
  const { isAdminOrMaintainer, user } = useUserStore();
  const { client } = useApi();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const [hasAcknowledgedPackingRestart, setHasAcknowledgedPackingRestart] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const { showToast } = useToastContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIsAdmin = isAdmin(user);
  const requestedTab = (searchParams.get('tab') ?? SERVERS_PAGE_TAB.servers) as ServersPageTab;
  const activeTab: ServersPageTab =
    requestedTab === SERVERS_PAGE_TAB.dataSync && userIsAdmin
      ? SERVERS_PAGE_TAB.dataSync
      : SERVERS_PAGE_TAB.servers;

  // Ingestion state
  const [syncType, setSyncType] = useState<SyncType | 'all'>('all');
  const [syncFilePath, setSyncFilePath] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);

  const serverColumns: TableColumn<{ name: string; group: 'packing' | 'standard' }>[] = [
    {
      key: 'name',
      label: t('serversPage.display.name'),
      sortable: true,
      render: (value) => <span className="text-core-slate-1300 font-medium">{value}</span>,
    },
  ];

  if (!isAdminOrMaintainer()) {
    return <NotFoundPage />;
  }

  const handleRowClick = (row: { name: string }) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setHasAcknowledgedPackingRestart(false);
    setSelectedServer(row.name);
    setIsDialogOpen(true);
  };

  const handleClose = () => setIsDialogOpen(false);

  const handleRestart = async () => {
    if (!selectedServer) return;
    setIsRestarting(true);
    try {
      const res = await client.v1.servers[':name'].restart.$post({
        param: { name: selectedServer },
      });
      if (res.status === 201) {
        showToast(t('serversPage.response.restartSuccess'), 'success', false);
      } else {
        const message = await getResponseErrorMessage(res, t('serversPage.response.restartFailed'));
        showToast(message, 'error');
      }
    } catch (e) {
      showToast(toErrorMessage(e, t('serversPage.response.restartFailed')), 'error');
    } finally {
      setIsRestarting(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const body =
        syncType === 'all'
          ? {}
          : syncFilePath.trim()
            ? { type: syncType, filePath: syncFilePath.trim() }
            : { type: syncType };

      const res = await client.v1.ingestion.trigger.$post({ json: body as never });

      if (res.status === 202) {
        const label =
          syncType === 'all'
            ? t('serversPage.ingestion.typeAll')
            : (SYNC_TYPES.find((s) => s.value === syncType)?.label ?? syncType);
        showToast(
          syncFilePath.trim()
            ? t('serversPage.ingestion.syncStartedFile', {
                type: label,
                filePath: syncFilePath.trim(),
              })
            : t('serversPage.ingestion.syncStarted', { type: label }),
          'success',
          false
        );
      } else {
        const message = await getResponseErrorMessage(res, t('serversPage.ingestion.syncFailed'));
        showToast(message, 'error');
      }
    } catch (e) {
      showToast(toErrorMessage(e, t('serversPage.ingestion.syncFailed')), 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const setTab = (tab: ServersPageTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === SERVERS_PAGE_TAB.servers) {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const nextTab =
      activeTab === SERVERS_PAGE_TAB.servers ? SERVERS_PAGE_TAB.dataSync : SERVERS_PAGE_TAB.servers;
    setTab(nextTab);
    event.currentTarget.querySelector<HTMLButtonElement>(`#servers-tab-${nextTab}`)?.focus();
  };

  const filePathApplies = syncType === 'graphics' || syncType === 'labels';
  const selectedServerIsPacking = selectedServer !== null && isPackingServer(selectedServer);
  const serverRows = [...SERVERS]
    .sort(
      (left, right) =>
        Number(isPackingServer(right)) - Number(isPackingServer(left)) ||
        left.localeCompare(right, undefined, { numeric: true })
    )
    .map((server) => ({
      name: server,
      group: isPackingServer(server) ? ('packing' as const) : ('standard' as const),
    }));
  const standardServerRows = serverRows.filter((server) => server.group === 'standard');
  const packingServerRows = serverRows.filter((server) => server.group === 'packing');

  const tabClass = (tab: ServersPageTab) =>
    `px-300 py-150 text-body-sm font-brand font-bold border-b-25 -mb-25 transition-colors ${
      activeTab === tab
        ? 'border-core-blue-900 text-core-blue-900'
        : 'border-transparent text-core-slate-1100 hover:text-core-slate-1300'
    }`;

  const serversList = (
    <div className="grid gap-300 lg:grid-cols-2">
      <Card
        appearance="outline"
        className="rounded-200 border border-core-slate-300 bg-white p-200 mb-200"
      >
        <div className="mb-200 p-200">
          <h2 className="text-heading-sm font-brand font-bold text-core-slate-1300">
            {t('serversPage.display.standard')}
          </h2>
          <p className="text-body-xs text-core-slate-1000">
            {t('serversPage.display.standardGroupDescription')}
          </p>
        </div>
        <div className="px-200 mb-100">
          <Table
            data={standardServerRows}
            columns={serverColumns}
            onRowClick={handleRowClick}
            searchable={false}
            emptyMessage={t('serversPage.display.noServersFound')}
            ariaLabel={t('serversPage.display.standard')}
          />
        </div>
      </Card>
      {packingServerRows.length > 0 && (
        <Card
          appearance="outline"
          className="rounded-200 border border-core-slate-300  p-200 mb-200"
        >
          <div className="bg-core-slate-200">
            <div className="mb-200 p-200">
              <h2 className="text-heading-sm font-brand font-bold text-core-slate-1300">
                {t('serversPage.display.packing')}
              </h2>
              <p className="text-body-xs text-core-slate-1000">
                {t('serversPage.display.packingGroupDescription', {
                  count: packingProdServers.length,
                })}
              </p>
            </div>
            <div className="px-200 mb-100">
              <Table
                data={packingServerRows}
                columns={serverColumns}
                onRowClick={handleRowClick}
                searchable={false}
                emptyMessage={t('serversPage.display.noServersFound')}
                ariaLabel={t('serversPage.display.packing')}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <>
      <div className="px-400 py-300 flex flex-col gap-300">
        {userIsAdmin ? (
          <div
            role="tablist"
            aria-label={t('serversPage.display.serversTitle')}
            className="border-b-25 border-core-slate-100 flex gap-100"
            onKeyDown={handleTabKeyDown}
          >
            <button
              type="button"
              role="tab"
              id="servers-tab-servers"
              aria-selected={activeTab === SERVERS_PAGE_TAB.servers}
              aria-controls="servers-panel-servers"
              tabIndex={activeTab === SERVERS_PAGE_TAB.servers ? 0 : -1}
              className={tabClass(SERVERS_PAGE_TAB.servers)}
              onClick={() => setTab(SERVERS_PAGE_TAB.servers)}
            >
              {t('serversPage.display.serversTitle')} ({SERVERS.length})
            </button>
            <button
              type="button"
              role="tab"
              id="servers-tab-data-sync"
              aria-selected={activeTab === SERVERS_PAGE_TAB.dataSync}
              aria-controls="servers-panel-data-sync"
              tabIndex={activeTab === SERVERS_PAGE_TAB.dataSync ? 0 : -1}
              className={tabClass(SERVERS_PAGE_TAB.dataSync)}
              onClick={() => setTab(SERVERS_PAGE_TAB.dataSync)}
            >
              {t('serversPage.ingestion.title')}
            </button>
          </div>
        ) : (
          <h1 className="text-heading-md font-brand font-bold text-core-slate-1300">
            {`${t('serversPage.display.serversTitle')} (${SERVERS.length})`}
          </h1>
        )}
        {userIsAdmin ? (
          <div
            role="tabpanel"
            id="servers-panel-servers"
            aria-labelledby="servers-tab-servers"
            hidden={activeTab !== SERVERS_PAGE_TAB.servers}
          >
            {serversList}
          </div>
        ) : (
          serversList
        )}
        {userIsAdmin && (
          <div
            role="tabpanel"
            id="servers-panel-data-sync"
            aria-labelledby="servers-tab-data-sync"
            hidden={activeTab !== SERVERS_PAGE_TAB.dataSync}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: Tab panels starting with non-focusable content need a keyboard tab stop.
            tabIndex={0}
            className="border border-core-slate-300 rounded-200 p-400"
          >
            <h2 className="text-heading-sm font-brand font-bold text-core-slate-1300 mb-100">
              {t('serversPage.ingestion.title')}
            </h2>
            <p className="text-body-xs text-core-slate-1100 mb-300">
              {t('serversPage.ingestion.description')}
            </p>

            <div className="flex flex-col gap-300">
              <div className="flex flex-col gap-100">
                <span className="text-body-xs font-medium text-core-slate-1200">
                  {t('serversPage.ingestion.typeLabel')}
                </span>
                <div className="flex gap-150 flex-wrap">
                  {[
                    { value: 'all' as const, label: t('serversPage.ingestion.typeAll') },
                    ...SYNC_TYPES,
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSyncType(option.value);
                        setSyncFilePath('');
                      }}
                      className={[
                        'px-200 py-100 rounded-100 text-body-xs font-medium border transition-colors',
                        syncType === option.value
                          ? 'bg-core-blue-900 text-core-white border-core-blue-900'
                          : 'bg-core-white text-core-slate-1200 border-core-slate-300 hover:border-core-slate-500',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {filePathApplies && (
                <div className="max-w-md">
                  <Label>{t('serversPage.ingestion.filePathLabel')}</Label>
                  <TextField
                    placeholder={
                      syncType === 'graphics'
                        ? '/Graphics/combi/logo.png'
                        : '/Labels/combi/my-label.btw'
                    }
                    value={syncFilePath}
                    onChange={(_e, value) => setSyncFilePath(value)}
                  />
                  <HelpText>{t('serversPage.ingestion.filePathHelp')}</HelpText>
                </div>
              )}

              <div>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSync}
                  loading={isSyncing}
                  disabled={isSyncing}
                  loadingText={t('serversPage.ingestion.syncing')}
                  className="min-w-[140px]"
                >
                  {t('serversPage.ingestion.triggerButton')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isDialogOpen} onClose={handleClose} triggerRef={triggerRef} size="medium">
        <Modal.Title>
          {selectedServer
            ? `${t('serversPage.display.serversTitle').slice(0, -1)}: ${selectedServer}`
            : ''}
        </Modal.Title>
        <Modal.Content>
          {selectedServer && (
            <div className="flex flex-col gap-300 text-body-xs">
              <div>
                <h2 className="text-heading-sm font-brand font-bold text-core-slate-1100 mb-100">
                  {t('serversPage.display.restartServicesTitle')}
                </h2>
                <p className="text-core-slate-1200 mb-200">
                  <Trans
                    i18nKey="serversPage.display.restartServicesDesc"
                    components={{ 1: <span className="font-bold" /> }}
                    values={{ server: selectedServer }}
                  />
                </p>
                <div className="flex items-center gap-200 mb-250 flex-wrap">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={SERVER_DASHBOARD_URL}
                    className="text-core-blue-900 text-body-xs font-medium underline underline-offset-4 hover:text-core-blue-700 focus:outline-none focus:ring-2 focus:ring-core-blue-600 rounded-100 px-50"
                  >
                    {t('serversPage.display.viewServerDashboard')}
                  </a>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleRestart}
                    loading={isRestarting}
                    disabled={
                      isRestarting || (selectedServerIsPacking && !hasAcknowledgedPackingRestart)
                    }
                    loadingText={t('serversPage.display.restarting')}
                    className="min-w-[140px]"
                  >
                    {t('serversPage.display.restartServices')}
                  </Button>
                </div>
                {selectedServerIsPacking && (
                  <div className="rounded-100 border border-core-yellow-300 bg-core-yellow-50 p-200 text-core-yellow-1100">
                    <p className="mb-150 font-semibold">
                      {t('serversPage.display.packingRestartWarning')}
                    </p>
                    <Checkbox
                      label={t('serversPage.display.packingRestartConfirmation')}
                      checked={hasAcknowledgedPackingRestart}
                      onChange={(_event, checked) => setHasAcknowledgedPackingRestart(checked)}
                    />
                  </div>
                )}
                {isRestarting && (
                  <div className="mt-100 p-100 bg-core-yellow-50 border border-core-yellow-200 rounded-100 text-core-yellow-1100 text-body-xs max-w-sm">
                    {t('serversPage.display.restartingNotice')}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button variant="neutral" size="small" onClick={handleClose}>
            {t('serversPage.action.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
